/**
 * client-needs-tags — the bridge between a Client's drill-library "needs tags"
 * (Phase 2B) and Drill Library / Drill Planner filters.
 *
 * One Tag identity is shared across the app (drill-library taxonomy). These
 * helpers convert between three shapes:
 *   - needs tags:      ClientNeedsTag[]                 (stored on the client)
 *   - active filters:  { [category]: Set<string> }     (drill-library filter state)
 *   - drill ranking:   relaxed-OR match count           (library/planner ordering)
 *
 * See docs/features/260615-client-management/PLAN.md → "Unified tag system".
 */

import type { Client, ClientNeedsTag } from '@/lib/types';
import { safeToDate } from '@/lib/utils';
import { parseClassificationString } from './classification-tags';
import type { OverallClassification } from '@/lib/posture-analysis/types';
import {
  buildDrillSuggestionFilters,
  type DrillSuggestionFilters,
} from '@/lib/posture-analysis/drill-suggestion-filters';
import type { Drill } from '@/lib/drill-taxonomy/drill-types';
import { drillSatisfiesFilterValue } from '@/lib/drill-taxonomy/drill-filter-match';
import { normalizePainLevelNrs } from './roster-fields';

/**
 * Taxonomy categories that round-trip as client "needs" (curated, configurable).
 * Primary/classification dimensions (Phase, lateralization, grounding) are NOT
 * needs — they live on the client's classification and expand via filter rules.
 */
export const NEEDS_CATEGORIES = [
  'Biomechanical Intent',
  'Movement Limitation',
  'Posture',
  'Phase',
] as const;

export type NeedsCategory = (typeof NEEDS_CATEGORIES)[number];

/** Canonical dedupe key for a needs tag. */
export function needsTagKey(tag: ClientNeedsTag): string {
  return `${tag.category}::${tag.value}`;
}

/** Trim + dedupe needs tags by `category::value`, dropping empty entries. */
export function dedupeNeedsTags(tags: ClientNeedsTag[]): ClientNeedsTag[] {
  const seen = new Set<string>();
  const result: ClientNeedsTag[] = [];
  for (const tag of tags) {
    const category = tag.category?.trim();
    const value = tag.value?.trim();
    if (!category || !value) continue;
    const key = `${category}::${value}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ category, value });
  }
  return result;
}

/** Needs tags → drill-library active-filter map ({ category: Set<value> }). */
export function needsTagsToActiveFilters(
  needs: ClientNeedsTag[]
): { [category: string]: Set<string> } {
  const filters: { [category: string]: Set<string> } = {};
  for (const { category, value } of dedupeNeedsTags(needs)) {
    (filters[category] ??= new Set<string>()).add(value);
  }
  return filters;
}

/**
 * Drill-library active filters → needs tags. Only categories in the allow-list
 * (default {@link NEEDS_CATEGORIES}) are written back to the client, so toggling
 * unrelated primary filters never pollutes the client's needs.
 */
export function activeFiltersToNeedsTags(
  activeFilters: { [category: string]: Set<string> | string[] | undefined },
  options?: { categories?: readonly string[] }
): ClientNeedsTag[] {
  const allow = new Set<string>(options?.categories ?? NEEDS_CATEGORIES);
  const tags: ClientNeedsTag[] = [];
  for (const [category, values] of Object.entries(activeFilters)) {
    if (!allow.has(category) || !values) continue;
    for (const value of values) {
      tags.push({ category, value });
    }
  }
  return dedupeNeedsTags(tags);
}

/** Convert posture/SoP suggestion filters into needs tags (allow-listed categories only). */
export function drillSuggestionFiltersToNeedsTags(
  filters: DrillSuggestionFilters
): ClientNeedsTag[] {
  const tags: ClientNeedsTag[] = [
    ...filters.postureLabels.map((value) => ({ category: 'Posture', value })),
    ...filters.biomechanicalIntents.map((value) => ({ category: 'Biomechanical Intent', value })),
    ...filters.movementLimitations.map((value) => ({ category: 'Movement Limitation', value })),
  ];
  return dedupeNeedsTags(tags);
}

/**
 * Suggested needs tags derived from a client's latest posture/SoP classification.
 * Wraps {@link buildDrillSuggestionFilters}; used to auto-populate the Needs panel
 * and for on-demand "suggest needs".
 *
 * Includes Posture, Biomechanical Intent, Movement Limitation, and Phase — each of
 * which maps to a Drill Library / Planner filter. Laterality and grounding are
 * intentionally excluded (no corresponding Library filter). Returns an empty list
 * when the client has no classification signal at all (so a brand-new client is
 * not seeded with a default Phase).
 */
export function seedNeedsFromClassification(client: Client): ClientNeedsTag[] {
  const filters = buildFiltersFromClient(client);
  const base = drillSuggestionFiltersToNeedsTags(filters);
  const hasClassificationSignal =
    base.length > 0 || parseClassificationString(client.classification).length > 0;
  if (!hasClassificationSignal) return [];
  return dedupeNeedsTags([...base, { category: 'Phase', value: filters.phaseTag }]);
}

const PLANNER_POSTURE_LABELS = ['Anterior Orientation', 'Swayback'] as const;

/**
 * Merge a client's curated needs into classification-derived planner filters.
 * Needs add to the matching `DrillSuggestionFilters` arrays (Movement Limitation,
 * Biomechanical Intent, and the two planner Posture labels) so the Drill Planner
 * suggestion flow reflects manual curation, not just the assessment.
 */
export function mergeNeedsIntoFilters(
  filters: DrillSuggestionFilters,
  needs: ClientNeedsTag[]
): DrillSuggestionFilters {
  const movementLimitations = new Set(filters.movementLimitations);
  const biomechanicalIntents = new Set(filters.biomechanicalIntents);
  const postureLabels = new Set(filters.postureLabels);

  for (const { category, value } of dedupeNeedsTags(needs)) {
    if (category === 'Movement Limitation') {
      movementLimitations.add(value);
    } else if (category === 'Biomechanical Intent') {
      biomechanicalIntents.add(value);
    } else if (
      category === 'Posture' &&
      (PLANNER_POSTURE_LABELS as readonly string[]).includes(value)
    ) {
      postureLabels.add(value as (typeof PLANNER_POSTURE_LABELS)[number]);
    }
  }

  return {
    ...filters,
    movementLimitations: [...movementLimitations],
    biomechanicalIntents: [...biomechanicalIntents],
    postureLabels: [...postureLabels],
  };
}

/**
 * Planner suggestion filters for a client: classification-derived filters
 * ({@link buildFiltersFromClient}) augmented with the client's curated needs.
 */
export function buildClientPlannerFilters(client: Client): DrillSuggestionFilters {
  return mergeNeedsIntoFilters(buildFiltersFromClient(client), client.needsTags ?? []);
}

/**
 * Rank drills by how many of the client's needs they satisfy (relaxed OR).
 * Drills matching more needs come first; ties preserve input order (stable).
 *
 * - No needs → returns all drills unchanged.
 * - Needs present but nothing matches → falls back to all drills (never empty),
 *   unless `fallbackToAll` is false, in which case an empty list is returned.
 */
export function rankDrillsByNeedsMatch(
  drills: Drill[],
  needs: ClientNeedsTag[],
  options?: { fallbackToAll?: boolean }
): Drill[] {
  const cleanNeeds = dedupeNeedsTags(needs);
  if (cleanNeeds.length === 0) return [...drills];

  const scored = drills.map((drill, index) => ({
    drill,
    index,
    score: cleanNeeds.reduce(
      (count, need) =>
        drillSatisfiesFilterValue(drill, need.category, need.value) ? count + 1 : count,
      0
    ),
  }));

  const matched = scored.filter((entry) => entry.score > 0);

  if (matched.length === 0) {
    return options?.fallbackToAll === false ? [] : [...drills];
  }

  return matched
    .sort((a, b) => (b.score - a.score) || (a.index - b.index))
    .map((entry) => entry.drill);
}

// ---------------------------------------------------------------------------
// Client → OverallClassification adapter (best-effort from stored data).
// Shared by SuggestDrillsButton (planner) and seedNeedsFromClassification.
// ---------------------------------------------------------------------------

export function buildFiltersFromClient(client: Client): DrillSuggestionFilters {
  const latestPosture = client.postureAnalyses
    ?.slice()
    .sort((a, b) => {
      // Use safeToDate, not raw toMillis(): SSR-hydrated / API-normalized docs
      // arrive as { _seconds } objects or ISO strings without Timestamp methods,
      // which would collapse the sort to array order and pick a stale analysis.
      const ta = safeToDate(a.createdAt)?.getTime() ?? 0;
      const tb = safeToDate(b.createdAt)?.getTime() ?? 0;
      return tb - ta;
    })[0];

  // `postureAnalyses` is only loaded on the Client 360 profile; the roster cache
  // (used when opening the Drill Library) carries just the `classification` string.
  // Prefer the richer posture analysis when present, else fall back to the badge
  // tokens (APT / R Lat / R Grd …) so library pre-filtering works from any entry point.
  const classificationTagIds = new Set(
    parseClassificationString(client.classification).map((tag) => tag.id)
  );
  const orientationFromString: 'APT' | 'SB' | 'N' = classificationTagIds.has('APT')
    ? 'APT'
    : classificationTagIds.has('SB')
      ? 'SB'
      : 'N';
  const lateralizedFromString: 'Left' | 'Right' | null = classificationTagIds.has('R Lat')
    ? 'Right'
    : classificationTagIds.has('L Lat')
      ? 'Left'
      : null;
  const groundedFromString: 'Left' | 'Right' | null = classificationTagIds.has('R Grd')
    ? 'Right'
    : classificationTagIds.has('L Grd')
      ? 'Left'
      : null;

  const pattern = latestPosture?.classification?.pattern
    ? resolvePattern(latestPosture.classification.pattern)
    : orientationFromString;
  const lateralized = latestPosture?.classification?.lateralized
    ? resolveLateralization(latestPosture.classification.lateralized)
    : lateralizedFromString;
  const grounded = latestPosture?.classification?.groundingSide
    ? resolveLateralization(latestPosture.classification.groundingSide)
    : groundedFromString;
  const phase = resolvePhase(client.classification, latestPosture?.classification?.pattern);
  const movementLimitations = resolveMovementLimitations(client);

  const overall: OverallClassification = {
    pattern,
    lateralized,
    grounded,
    phase,
    shortCode: [pattern, lateralized ? `${lateralized[0]} Lat` : null, phase]
      .filter(Boolean)
      .join(', '),
    movementLimitations,
    confidence: { pattern: 0.75, lateralization: 0.6 },
  };

  return buildDrillSuggestionFilters(overall, {
    liftingExperience: client.liftingExperience,
    painLevel: normalizePainLevelNrs(client),
  });
}

function resolvePattern(raw?: string): 'APT' | 'SB' | 'N' {
  if (!raw) return 'N';
  const u = raw.toUpperCase();
  if (u.includes('APT') || u.includes('ANTERIOR PELVIC')) return 'APT';
  if (u.includes('SB') || u.includes('SWAYBACK')) return 'SB';
  return 'N';
}

/**
 * The Posture taxonomy label that contradicts a client's current global
 * orientation. APT (≈ "Anterior Orientation") and SB ("Swayback") are mutually
 * exclusive (`pattern: 'APT' | 'SB' | 'N'`), so stamping the opposite as a
 * "need" is self-contradicting. Returns null when no orientation is resolved.
 */
export function contradictoryPostureLabel(
  client: Pick<Client, 'classification'>
): string | null {
  const pattern = resolvePattern(client.classification);
  if (pattern === 'APT') return 'Swayback';
  if (pattern === 'SB') return 'Anterior Orientation';
  return null;
}

function resolveLateralization(raw?: string): 'Left' | 'Right' | null {
  if (!raw) return null;
  const l = raw.toLowerCase();
  if (l.includes('left')) return 'Left';
  if (l.includes('right')) return 'Right';
  return null;
}

function resolvePhase(classification?: string, pattern?: string): 'P1' | 'P2' | 'P3' {
  if (classification) {
    const m = classification.match(/P([123])/i);
    if (m) return `P${m[1]}` as 'P1' | 'P2' | 'P3';
  }
  if (pattern) {
    const m = pattern.match(/P([123])/i);
    if (m) return `P${m[1]}` as 'P1' | 'P2' | 'P3';
  }
  return 'P1';
}

function resolveMovementLimitations(client: Client): string[] {
  const assessments = client.assessments;
  if (!assessments?.length) return [];
  const sorted = [...assessments].sort((a, b) => {
    const ta = safeToDate((a as { createdAt?: unknown }).createdAt)?.getTime() ?? 0;
    const tb = safeToDate((b as { createdAt?: unknown }).createdAt)?.getTime() ?? 0;
    return tb - ta;
  });
  const latest = sorted[0] as { movementLimitations?: unknown };
  if (Array.isArray(latest?.movementLimitations)) {
    return latest.movementLimitations.filter((v): v is string => typeof v === 'string');
  }
  return [];
}
